import React, { useState, useRef, useEffect } from "react";
import Context from "../../Context";
import {
  X,
  ShieldAlert,
  CheckCircle2,
  AlertCircle,
  Play,
  Square,
  Sparkles,
} from "lucide-react";

export const BatchAnonymizeModal = ({
  isOpen,
  onClose,
  totalPending,
  onSuccess,
}) => {
  const { callApi } = Context();
  const [phase, setPhase] = useState("confirm"); // "confirm" | "processing" | "done" | "error"
  const [processedCount, setProcessedCount] = useState(0);
  const [remainingCount, setRemainingCount] = useState(totalPending || 0);
  const [initialTarget, setInitialTarget] = useState(totalPending || 0);
  const [errorMessage, setErrorMessage] = useState("");
  const isRunningRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setPhase("confirm");
      setProcessedCount(0);
      setRemainingCount(totalPending || 0);
      setInitialTarget(totalPending || 0);
      setErrorMessage("");
      isRunningRef.current = false;
    }
  }, [isOpen, totalPending]);

  if (!isOpen) return null;

  const startBatchProcess = async () => {
    setPhase("processing");
    isRunningRef.current = true;
    let accumulatedDone = 0;

    try {
      while (isRunningRef.current) {
        const res = await callApi("users-batchAnonymizeUsers", { limit: 200 });

        if (!res?.status) {
          setErrorMessage(res?.message || "เกิดข้อผิดพลาดในการประมวลผล");
          setPhase("error");
          isRunningRef.current = false;
          return;
        }

        if (res.count === 0) {
          break;
        }

        accumulatedDone += res.count;
        setProcessedCount(accumulatedDone);

        const rem = typeof res.remaining === "number" ? res.remaining : 0;
        setRemainingCount(rem);

        if (rem <= 0) {
          break;
        }

        // Delay เล็กน้อยเพื่อความสมูทของ UI (150ms)
        await new Promise((resolve) => setTimeout(resolve, 150));
      }

      isRunningRef.current = false;
      setPhase("done");
      if (onSuccess) {
        onSuccess(accumulatedDone);
      }
    } catch (err) {
      console.error("Batch anonymize error:", err);
      setErrorMessage("เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์ กรุณาลองใหม่อีกครั้ง");
      setPhase("error");
      isRunningRef.current = false;
    }
  };

  const handleStop = () => {
    isRunningRef.current = false;
    setPhase("done");
    if (onSuccess) {
      onSuccess(processedCount);
    }
  };

  const progressPercent =
    initialTarget > 0
      ? Math.min(Math.round((processedCount / initialTarget) * 100), 100)
      : processedCount > 0
      ? 100
      : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 overflow-y-auto animate-in fade-in-50 duration-150">
      <div className="bg-white w-full max-w-lg rounded-md shadow-2xl border border-slate-200 overflow-hidden my-8 animate-in zoom-in-95 duration-150">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50/90">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-rose-100 text-rose-700 rounded-md shrink-0">
              <ShieldAlert className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-800 leading-tight">
                เซนเซอร์ข้อมูลส่วนบุคคล (PDPA)
              </h3>
              <p className="text-xs text-slate-500 font-medium">
                ระบบจัดการลบบัญชีและล้างข้อมูลส่วนบุคคลอัตโนมัติ
              </p>
            </div>
          </div>

          {phase !== "processing" && (
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-md transition cursor-pointer"
              title="ปิดหน้าต่าง"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Modal Body */}
        <div className="p-6">
          {/* Phase 1: Confirm */}
          {phase === "confirm" && (
            <div className="space-y-4">
              <div className="p-4 bg-rose-50 border border-rose-200 rounded-md">
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-rose-500 text-white rounded-md shrink-0 mt-0.5">
                    <ShieldAlert className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-rose-950">
                      มีผู้ใช้สถานะ Removed รอการเซนเซอร์ {initialTarget.toLocaleString()} บัญชี
                    </h4>
                    <p className="text-xs text-rose-800 mt-1 leading-relaxed">
                      ระบบจะทำการประมวลผลกวาดลบทีละก้อน (ก้อนละ 200 บัญชี) แบบคู่ขนานและวนลูปต่อเนื่องให้อัตโนมัติจนกว่าจะครบหมดทุกบัญชี
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 rounded-md p-3.5 border border-slate-200 space-y-2 text-xs text-slate-600">
                <p className="font-bold text-slate-800 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-blue" />
                  <span>สิ่งที่จะดำเนินการตามมาตรฐาน PDPA:</span>
                </p>
                <ul className="list-disc list-inside space-y-1 pl-1 text-slate-600">
                  <li>เซนเซอร์ชื่อ-นามสกุลเป็น <span className="font-semibold text-slate-800">ผู้ใช้งาน (ลบบัญชี) (วันดี)</span></li>
                  <li>Mask เบอร์โทรศัพท์เหลือเพียงหัวท้าย (เช่น <span className="font-semibold font-mono">081xxxx123</span>)</li>
                  <li>ล้างข้อมูลความลับทั้งหมด: บัญชีธนาคาร, LINE, รหัสผ่าน และ Token การล็อกอิน</li>
                  <li>ปรับสถานะผู้ใช้เป็น <span className="font-semibold text-slate-800 font-mono">censored</span> ป้องกันการใช้งานระบบถาวร</li>
                </ul>
              </div>
            </div>
          )}

          {/* Phase 2: Processing */}
          {phase === "processing" && (
            <div className="py-4 flex flex-col items-center text-center space-y-5">
              {/* Navy Loading Spinner */}
              <div className="relative flex items-center justify-center">
                <div className="w-14 h-14 border-4 border-slate-200 border-t-[#003A9E] rounded-full animate-spin" />
                <div className="absolute w-3 h-3 bg-rose-500 rounded-full animate-ping" />
              </div>

              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-xs font-bold mb-2">
                  <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
                  <span>กำลังกวาดเซนเซอร์ข้อมูลทีละก้อน...</span>
                </div>
                <h4 className="text-2xl font-bold text-slate-900 font-mono">
                  {processedCount.toLocaleString()} <span className="text-sm font-normal text-slate-500">/ {initialTarget.toLocaleString()} บัญชี</span>
                </h4>
                <p className="text-xs text-slate-500 mt-1 font-semibold">
                  เหลืออีก {remainingCount.toLocaleString()} บัญชี ({progressPercent}%)
                </p>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-slate-100 rounded-md h-3.5 overflow-hidden border border-slate-200">
                <div
                  className="bg-emerald-500 h-full rounded-md transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          )}

          {/* Phase 3: Done */}
          {phase === "done" && (
            <div className="py-4 flex flex-col items-center text-center space-y-4">
              <div className="w-14 h-14 rounded-md bg-emerald-100 text-emerald-600 flex items-center justify-center shadow-xs">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <div>
                <h4 className="text-xl font-bold text-slate-900">
                  ดำเนินการเซนเซอร์ข้อมูลสำเร็จ!
                </h4>
                <p className="text-sm font-semibold text-slate-600 mt-1">
                  ระบบได้ทำการกวาดล้างข้อมูลส่วนบุคคลครบถ้วนทั้งหมดแล้ว
                </p>
                <div className="mt-3 inline-block px-3 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold">
                  รวมทั้งสิ้น {processedCount.toLocaleString()} บัญชี
                </div>
              </div>
            </div>
          )}

          {/* Phase 4: Error */}
          {phase === "error" && (
            <div className="py-4 flex flex-col items-center text-center space-y-4">
              <div className="w-14 h-14 rounded-md bg-rose-100 text-rose-600 flex items-center justify-center shadow-xs">
                <AlertCircle className="w-8 h-8" />
              </div>
              <div>
                <h4 className="text-xl font-bold text-rose-900">
                  เกิดข้อผิดพลาด
                </h4>
                <p className="text-sm font-semibold text-rose-700 mt-1">
                  {errorMessage || "ไม่สามารถเชื่อมต่อกับระบบได้"}
                </p>
                {processedCount > 0 && (
                  <p className="text-xs text-slate-500 mt-2">
                    (ทำรายการสำเร็จไปแล้ว {processedCount.toLocaleString()} บัญชี)
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-3.5 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2.5">
          {phase === "confirm" && (
            <>
              <button
                type="button"
                onClick={onClose}
                className="py-2.5 px-4 rounded-md text-sm font-semibold text-slate-600 hover:text-slate-800 bg-white hover:bg-slate-100 border border-slate-200 transition cursor-pointer"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                onClick={startBatchProcess}
                className="py-2.5 px-4 bg-rose-600 hover:bg-rose-700 active:opacity-95 text-white text-sm font-bold rounded-md shadow-xs transition flex items-center gap-2 cursor-pointer"
              >
                <Play className="w-4 h-4 fill-white" />
                <span>เริ่มกวาดลบอัตโนมัติ</span>
              </button>
            </>
          )}

          {phase === "processing" && (
            <button
              type="button"
              onClick={handleStop}
              className="py-2 px-3.5 bg-white hover:bg-slate-100 text-slate-700 text-sm font-semibold rounded-md border border-slate-300 transition flex items-center gap-1.5 cursor-pointer"
            >
              <Square className="w-3.5 h-3.5 fill-slate-700 text-slate-700" />
              <span>หยุดชั่วคราว</span>
            </button>
          )}

          {phase === "done" && (
            <button
              type="button"
              onClick={onClose}
              className="py-2.5 px-5 bg-emerald-600 hover:bg-emerald-700 active:opacity-95 text-white text-sm font-bold rounded-md shadow-xs transition cursor-pointer"
            >
              เสร็จสิ้น (ปิดหน้าต่าง)
            </button>
          )}

          {phase === "error" && (
            <>
              <button
                type="button"
                onClick={onClose}
                className="py-2.5 px-4 rounded-md text-sm font-semibold text-slate-600 hover:text-slate-800 bg-white hover:bg-slate-100 border border-slate-200 transition cursor-pointer"
              >
                ปิด
              </button>
              <button
                type="button"
                onClick={startBatchProcess}
                className="py-2.5 px-4 bg-rose-600 hover:bg-rose-700 active:opacity-95 text-white text-sm font-bold rounded-md shadow-xs transition cursor-pointer"
              >
                ลองใหม่อีกครั้ง
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
